import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Switch, TextField,
  Tooltip, Typography, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, DragIndicator as DragIcon, Edit as EditIcon, Restaurant as DishIcon,
  Star as StarIcon, VisibilityOff as HiddenIcon,
} from '@mui/icons-material';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MenuService from '../../services/menu.service';
import DishDialog from './DishDialog';
import { apiError, formatPrice, startingPrice } from './menuUtils';

/** Drag handle + row wrapper for @dnd-kit sortable lists. */
const Sortable = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <Box ref={setNodeRef} sx={{ transform: CSS.Transform.toString(transform), transition, position: 'relative', zIndex: isDragging ? 2 : 'auto', opacity: isDragging ? 0.85 : 1 }}>
      {children({ handle: disabled ? null : { ...attributes, ...listeners } })}
    </Box>
  );
};

const DragHandle = ({ handle, label }) => (handle ? (
  <IconButton size="small" {...handle} aria-label={label} sx={{ cursor: 'grab', color: 'text.disabled', touchAction: 'none' }}>
    <DragIcon fontSize="small" />
  </IconButton>
) : null);

const DishRow = ({ item, currency, canEdit, handle, onEdit, onToggle, onDelete, t }) => {
  const from = startingPrice(item);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}
      sx={{ py: 1.25, px: 1, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' }, opacity: item.active ? 1 : 0.55 }}>
      <DragHandle handle={handle} label={t('menus.dragToReorder')} />
      <Box sx={{ width: 56, height: 56, borderRadius: 1.5, overflow: 'hidden', flex: 'none', bgcolor: 'action.selected', display: 'grid', placeItems: 'center' }}>
        {item.image_url
          ? <Box component="img" src={item.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <DishIcon sx={{ color: 'text.disabled' }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, cursor: canEdit ? 'pointer' : 'default' }} onClick={() => canEdit && onEdit(item)}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontWeight={600} noWrap>{item.name}</Typography>
          {item.featured && <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} titleAccess={t('menus.featured')} />}
          {!item.active && <HiddenIcon sx={{ fontSize: 16, color: 'text.secondary' }} titleAccess={t('menus.hidden')} />}
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>{item.description || ' '}</Typography>
      </Box>
      <Typography fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
        {from === null ? '—' : `${item.price_options?.length ? t('menus.from') + ' ' : ''}${formatPrice(currency, from)}`}
      </Typography>
      <Tooltip title={item.available ? t('menus.markSoldOut') : t('menus.markAvailable')}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { sm: 128 } }}>
          <Switch size="small" checked={!!item.available} disabled={!canEdit} onChange={() => onToggle(item)} inputProps={{ 'aria-label': t('menus.available') }} />
          <Typography variant="caption" color={item.available ? 'success.main' : 'error.main'} fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
            {item.available ? t('menus.availableShort') : t('menus.soldOut')}
          </Typography>
        </Box>
      </Tooltip>
      {canEdit && (
        <Box sx={{ display: 'flex' }}>
          <IconButton size="small" onClick={() => onEdit(item)} aria-label={t('menus.editDish')}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(item)} aria-label={t('menus.deleteDish')}><DeleteIcon fontSize="small" /></IconButton>
        </Box>
      )}
    </Stack>
  );
};

/**
 * Sections and dishes of one menu. Mutations are optimistic where cheap
 * (sold-out toggle, reordering) and reload the menu otherwise.
 */
const MenuDishes = ({ menu, setMenu, reload, canEdit, notify, t }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [sectionDialog, setSectionDialog] = useState({ open: false, section: null, name: '', description: '' });
  const [dishDialog, setDishDialog] = useState({ open: false, dish: null, section: null });
  const [saving, setSaving] = useState(false);
  const sections = menu.sections || [];

  const fail = (error) => notify(apiError(error, t('menus.errorSaving')), 'error');

  const patchItem = (uuid, patch) => setMenu((m) => ({
    ...m,
    sections: m.sections.map((s) => ({ ...s, items: s.items.map((i) => (i.uuid === uuid ? { ...i, ...patch } : i)) })),
  }));

  // ---- Sections ----
  const saveSection = async () => {
    const { section, name, description } = sectionDialog;
    if (!name.trim()) return;
    try {
      setSaving(true);
      const payload = { name: name.trim(), description: description.trim() || null };
      if (section) await MenuService.updateSection(menu.uuid, section.uuid, payload);
      else await MenuService.createSection(menu.uuid, payload);
      setSectionDialog({ open: false, section: null, name: '', description: '' });
      await reload();
    } catch (error) { fail(error); } finally { setSaving(false); }
  };

  const toggleSection = async (section) => {
    try {
      await MenuService.updateSection(menu.uuid, section.uuid, { active: !section.active });
      setMenu((m) => ({ ...m, sections: m.sections.map((s) => (s.uuid === section.uuid ? { ...s, active: !s.active } : s)) }));
      notify(t('menus.saved'));
    } catch (error) { fail(error); }
  };

  const deleteSection = async (section) => {
    if (!window.confirm(t('menus.deleteSectionConfirm', { name: section.name, count: section.items.length }))) return;
    try {
      await MenuService.deleteSection(menu.uuid, section.uuid);
      await reload();
      notify(t('menus.deleted'));
    } catch (error) { fail(error); }
  };

  const onSectionDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(sections, sections.findIndex((s) => s.uuid === active.id), sections.findIndex((s) => s.uuid === over.id));
    setMenu((m) => ({ ...m, sections: next }));
    try { await MenuService.reorderSections(menu.uuid, next.map((s) => s.uuid)); } catch (error) { fail(error); reload(); }
  };

  // ---- Dishes ----
  const onItemDragEnd = (section) => async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const items = arrayMove(section.items, section.items.findIndex((i) => i.uuid === active.id), section.items.findIndex((i) => i.uuid === over.id));
    setMenu((m) => ({ ...m, sections: m.sections.map((s) => (s.uuid === section.uuid ? { ...s, items } : s)) }));
    try { await MenuService.reorderItems(menu.uuid, section.uuid, items.map((i) => i.uuid)); } catch (error) { fail(error); reload(); }
  };

  const toggleAvailable = async (item) => {
    patchItem(item.uuid, { available: !item.available });
    try {
      await MenuService.updateItem(menu.uuid, item.uuid, { available: !item.available });
      notify(item.available ? t('menus.nowSoldOut', { name: item.name }) : t('menus.nowAvailable', { name: item.name }));
    } catch (error) { patchItem(item.uuid, { available: item.available }); fail(error); }
  };

  const saveDish = async (payload, image) => {
    try {
      setSaving(true);
      let uuid = dishDialog.dish?.uuid;
      if (uuid) await MenuService.updateItem(menu.uuid, uuid, payload);
      else uuid = (await MenuService.createItem(menu.uuid, payload)).data?.data?.uuid;
      if (uuid && image.file) await MenuService.uploadItemImage(menu.uuid, uuid, image.file);
      else if (uuid && image.removed && image.url) await MenuService.removeItemImage(menu.uuid, uuid);
      setDishDialog({ open: false, dish: null, section: null });
      await reload();
      notify(t('menus.saved'));
    } catch (error) { fail(error); } finally { setSaving(false); }
  };

  const deleteDish = async (item) => {
    if (!window.confirm(t('menus.deleteDishConfirm', { name: item.name }))) return;
    try {
      await MenuService.deleteItem(menu.uuid, item.uuid);
      setMenu((m) => ({ ...m, sections: m.sections.map((s) => ({ ...s, items: s.items.filter((i) => i.uuid !== item.uuid) })) }));
      notify(t('menus.deleted'));
    } catch (error) { fail(error); }
  };

  const openSection = (section = null) => setSectionDialog({ open: true, section, name: section?.name || '', description: section?.description || '' });

  return (
    <Box>
      {sections.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <DishIcon color="primary" sx={{ fontSize: 44 }} />
          <Typography variant="h6" fontWeight={700} mt={1}>{t('menus.noSectionsTitle')}</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>{t('menus.noSectionsHint')}</Typography>
          {canEdit && <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2.5 }} onClick={() => openSection()}>{t('menus.newSection')}</Button>}
        </Paper>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectionDragEnd}>
          <SortableContext items={sections.map((s) => s.uuid)} strategy={verticalListSortingStrategy}>
            <Stack spacing={2}>
              {sections.map((section) => (
                <Sortable key={section.uuid} id={section.uuid} disabled={!canEdit}>
                  {({ handle }) => (
                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', opacity: section.active ? 1 : 0.7 }}>
                      <Stack direction="row" alignItems="center" spacing={1}
                        sx={{ px: 1.5, py: 1.25, bgcolor: (th) => alpha(th.palette.primary.main, 0.05) }}>
                        <DragHandle handle={handle} label={t('menus.dragToReorder')} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" fontWeight={700} noWrap>{section.name}</Typography>
                            <Chip size="small" label={section.items.length} />
                            {!section.active && <Chip size="small" icon={<HiddenIcon />} label={t('menus.hidden')} />}
                          </Stack>
                          {section.description && <Typography variant="body2" color="text.secondary" noWrap>{section.description}</Typography>}
                        </Box>
                        {canEdit && (
                          <>
                            <Tooltip title={section.active ? t('menus.hideSection') : t('menus.showSection')}>
                              <Switch size="small" checked={!!section.active} onChange={() => toggleSection(section)} inputProps={{ 'aria-label': t('menus.visible') }} />
                            </Tooltip>
                            <IconButton size="small" onClick={() => openSection(section)} aria-label={t('menus.editSection')}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteSection(section)} aria-label={t('menus.deleteSection')}><DeleteIcon fontSize="small" /></IconButton>
                          </>
                        )}
                      </Stack>
                      <Box sx={{ px: 1, py: 1 }}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onItemDragEnd(section)}>
                          <SortableContext items={section.items.map((i) => i.uuid)} strategy={verticalListSortingStrategy}>
                            {section.items.map((item) => (
                              <Sortable key={item.uuid} id={item.uuid} disabled={!canEdit}>
                                {({ handle: itemHandle }) => (
                                  <DishRow item={item} currency={menu.currency_symbol} canEdit={canEdit} handle={itemHandle} t={t}
                                    onEdit={(dish) => setDishDialog({ open: true, dish, section: section.uuid })}
                                    onToggle={toggleAvailable} onDelete={deleteDish} />
                                )}
                              </Sortable>
                            ))}
                          </SortableContext>
                        </DndContext>
                        {section.items.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>{t('menus.sectionEmpty')}</Typography>
                        )}
                        {canEdit && (
                          <Button startIcon={<AddIcon />} sx={{ m: 1 }} onClick={() => setDishDialog({ open: true, dish: null, section: section.uuid })}>
                            {t('menus.addDish')}
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Sortable>
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      {canEdit && sections.length > 0 && (
        <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => openSection()}>{t('menus.newSection')}</Button>
      )}

      <Dialog open={sectionDialog.open} onClose={() => setSectionDialog({ ...sectionDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>{sectionDialog.section ? t('menus.editSection') : t('menus.newSection')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus required label={t('menus.sectionName')} placeholder={t('menus.sectionNamePlaceholder')} value={sectionDialog.name}
              inputProps={{ maxLength: 80 }} onChange={(e) => setSectionDialog({ ...sectionDialog, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') saveSection(); }} />
            <TextField label={t('menus.sectionDescription')} placeholder={t('menus.sectionDescriptionPlaceholder')} value={sectionDialog.description}
              inputProps={{ maxLength: 500 }} onChange={(e) => setSectionDialog({ ...sectionDialog, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialog({ ...sectionDialog, open: false })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={saveSection} disabled={saving || !sectionDialog.name.trim()}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <DishDialog open={dishDialog.open} dish={dishDialog.dish} sections={sections} sectionUuid={dishDialog.section}
        currency={menu.currency_symbol} saving={saving} t={t}
        onClose={() => setDishDialog({ open: false, dish: null, section: null })} onSave={saveDish} />
    </Box>
  );
};

export default MenuDishes;
